import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Checkbox, CopyButton, DownloadIcon, Field } from "@signet/ui";
import { api, ApiError, isMacDesktop, SignetMark } from "@signet/web-kit";

/* ──────────────────────────────────────────────────────────────────────────
   Signing in, as one conversation with the server.

   The server says which step comes next and the page never guesses: a
   password first, then either a code from the authenticator app, or (the
   first time) setting one up and saving recovery codes. The recovery codes
   are shown here once and never again, which is the whole point of them.
   ────────────────────────────────────────────────────────────────────────── */

type Step = "password" | "totp" | "totp-setup" | "recovery-codes";

export function LoginPage() {
  const search = useSearch({ from: "/login" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>("password");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [trust, setTrust] = useState(true);
  const [setup, setSetup] = useState<{ secret: string; qrSvg: string } | null>(null);
  const [codes, setCodes] = useState<string[]>([]);
  const [saved, setSaved] = useState(false);
  const codeInput = useRef<HTMLInputElement>(null);

  const finish = async () => {
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    // Who the tab was signed in as before may have been a creator's own
    // sign-in; the frame is chosen from this, so it must not outlive them.
    queryClient.removeQueries({ queryKey: ["bound"] });
    const next = search.next && search.next.startsWith("/") && !search.next.startsWith("//") ? search.next : "/";
    navigate({ to: next });
  };

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (step === "totp-setup" && !setup) {
      run(async () => setSetup(await api("/api/auth/totp/setup", { method: "POST", body: {} })));
    }
    if (step === "totp" || step === "totp-setup") setTimeout(() => codeInput.current?.focus(), 30);
  }, [step]);

  const submitPassword = (event: FormEvent) => {
    event.preventDefault();
    run(async () => {
      const { step: next } = await api<{ step: "done" | "totp" | "totp-setup" }>("/api/auth/login", { method: "POST", body: { username, password } });
      setPassword("");
      if (next === "done") await finish();
      else setStep(next);
    });
  };

  const submitSetup = (event: FormEvent) => {
    event.preventDefault();
    run(async () => {
      const { recoveryCodes } = await api<{ recoveryCodes: string[] }>("/api/auth/totp/confirm", { method: "POST", body: { code } });
      setCodes(recoveryCodes);
      setStep("recovery-codes");
    });
  };

  const submitTotp = (event: FormEvent) => {
    event.preventDefault();
    run(async () => {
      await api("/api/auth/totp/verify", { method: "POST", body: useRecovery ? { recoveryCode: code, trust } : { code, trust } });
      await finish();
    });
  };

  const downloadCodes = () => {
    const blob = new Blob([`Signet recovery codes for ${username}\nEach works once.\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "signet-recovery-codes.txt";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className={`login${isMacDesktop ? " mac-desktop" : ""}`}>
      <div className="login-card">
        <div className="login-mark">
          <SignetMark size={72} coin={false} />
        </div>

        {step === "password" ? (
          <form onSubmit={submitPassword} className="col login-form">
            <h1>Sign in to Signet</h1>
            <Field label="Username">
              <input autoFocus autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} required />
            </Field>
            <Field label="Password">
              <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </Field>
            {error ? <p className="login-error" role="alert">{error}</p> : null}
            <button className="button brass" disabled={busy || !username || !password}>{busy ? "Signing in" : "Sign in"}</button>
          </form>
        ) : null}

        {step === "totp-setup" ? (
          <form onSubmit={submitSetup} className="col login-form">
            <h1>Set up your authenticator</h1>
            <p className="login-lede">
              Signet needs a second step every time you sign in on a new device. Scan this with an authenticator app (1Password, Google Authenticator,
              Authy, the iPhone Passwords app), then type the six digits it shows.
            </p>
            {setup ? (
              <>
                <div className="qr" dangerouslySetInnerHTML={{ __html: setup.qrSvg }} aria-label="QR code for your authenticator app" role="img" />
                <details className="login-secret">
                  <summary>Can't scan it?</summary>
                  <p>Enter this key in the app instead:</p>
                  <div className="row">
                    <code>{setup.secret.match(/.{1,4}/g)?.join(" ")}</code>
                    <CopyButton value={setup.secret} label="Copy key" />
                  </div>
                </details>
              </>
            ) : (
              <div className="qr loading" />
            )}
            <Field label="Six-digit code">
              <input ref={codeInput} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]*" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} required />
            </Field>
            {error ? <p className="login-error" role="alert">{error}</p> : null}
            <button className="button brass" disabled={busy || code.replace(/\s/g, "").length < 6}>Confirm</button>
          </form>
        ) : null}

        {step === "recovery-codes" ? (
          <div className="col login-form">
            <h1>Save your recovery codes</h1>
            <p className="login-lede">If you lose your phone, each of these gets you in once. This is the only time Signet shows them.</p>
            <ol className="recovery-codes">{codes.map((c) => <li key={c}><code>{c}</code></li>)}</ol>
            <div className="row">
              <CopyButton value={codes.join("\n")} label="Copy codes" className="secondary-button tiny">Copy all</CopyButton>
              <button type="button" className="secondary-button tiny" onClick={downloadCodes}><DownloadIcon size={13} />Download</button>
            </div>
            <div className="row">
              <Checkbox checked={saved} onChange={setSaved} label="I've saved these somewhere safe" />
              <span className="checkbox-row" onClick={() => setSaved(!saved)}>I've saved these somewhere safe</span>
            </div>
            <button className="button brass" disabled={!saved} onClick={finish}>Continue to Signet</button>
          </div>
        ) : null}

        {step === "totp" ? (
          <form onSubmit={submitTotp} className="col login-form">
            <h1>{useRecovery ? "Use a recovery code" : "Enter your code"}</h1>
            <p className="login-lede">
              {useRecovery ? "Each recovery code works once." : "Open your authenticator app and type the six digits for Signet."}
            </p>
            <Field label={useRecovery ? "Recovery code" : "Six-digit code"}>
              <input
                ref={codeInput}
                inputMode={useRecovery ? "text" : "numeric"}
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </Field>
            <div className="row">
              <Checkbox checked={trust} onChange={setTrust} label="Remember this device for 30 days" />
              <span className="checkbox-row" onClick={() => setTrust(!trust)}>Remember this device for 30 days</span>
            </div>
            {error ? <p className="login-error" role="alert">{error}</p> : null}
            <button className="button brass" disabled={busy || !code.trim()}>Verify</button>
            <button type="button" className="text-button" onClick={() => { setUseRecovery(!useRecovery); setCode(""); setError(null); }}>
              {useRecovery ? "Use the authenticator app instead" : "Use a recovery code instead"}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}
